import { delay } from 'redux-saga'

import {
  select,
  fork,
  put,
  all,
  call,
  take,
  takeLatest,
  takeEvery,
  cancelled,
} from 'redux-saga/effects'

import T from 'i18n-react'

import {
  SUBMIT,
  SUBMIT_START,
  APPROVAL_TYPE,
  APPROVE_TYPE,
  SET_DIALOG_ACTIVE,
  SET_DIALOG_INACTIVE,
} from './constants'

import {
  clear,
  updateComment,
  updateActive,
  updateType,
  updateDialogType,
  setDialogActive,
  setDialogInactive,
  updateWorkflowId,
  updateProjectId,
  updateSkipables,
  submit,
  submitStart,
  resetActive,
} from './actions'

import {
  postApprovalReject,
  postApprovalAccept,
  postApprovalSkip,
} from 'containers/Approval/ApprovalPage/actions'

import {
  error
} from 'containers/App/Notification/actions'

import {
  commentSelector,
  activeSelector,
  typeSelector,
  dialogTypeSelector,
  typeLabelSelector,
  workflowIdSelector,
  projectIdSelector
} from './selectors'

import {
  approvalEntitySelector,
} from 'containers/Approval/ApprovalPage/selectors'

function* reset() {
  yield put(resetActive())
  yield call(delay, 500)
  yield put(clear())
}

function* handleSetDialogActive(action) {
  const { value } = action
  yield put(updateDialogType(value))
  yield put(updateActive(true))
}

function* handleSetDialogInactive() {
  yield call(reset)
}

function* handleAccept() {
  const type = yield select(typeSelector)
  const workflowId = yield select(workflowIdSelector)
  const projectId = yield select(projectIdSelector)
  if ([APPROVE_TYPE.SKIP_TO_KICKOFF.value, APPROVE_TYPE.SKIP_TO_DESIGN_REVIEW.value, APPROVE_TYPE.SKIP_TO_QUALIFICATION.value].includes(type)){
    yield put(postApprovalSkip(workflowId, projectId, type))
  } else if (type === APPROVE_TYPE.SUBMIT.value) {
    yield put(postApprovalAccept(workflowId, projectId))
  } else {
    throw 'Approval type was not set error'
  }
  yield call(reset)
}

function* handleReject() {
  const workflowId = yield select(workflowIdSelector)
  const projectId = yield select(projectIdSelector)
  const comment = yield select(commentSelector)
  if (comment) {
    yield put(postApprovalReject(workflowId, projectId, comment))
    yield call(reset)
  } else {
    yield put(error(T.translate('containers.approval.approvalDialog.rejectDialog.empty')))
  }
}

function* handleSubmit(action) {
  const dialogType = yield select(dialogTypeSelector)
  if (dialogType === APPROVAL_TYPE.APPROVE) {
    yield fork(handleAccept)
  } else {
    yield fork(handleReject)
  }
}

function* handleSubmitStart(action) {
  const { workflowId, projectId, value } = action
  const approvals = yield select(approvalEntitySelector)
  const approval = approvals[projectId]
  // if skippable than show dialog
  if (value === APPROVAL_TYPE.APPROVE && !approval.skipableToKickoff 
      && !approval.skipableToDesignReview && !approval.skipableToQualification) {
    yield put(postApprovalAccept(workflowId, projectId))
  } else {
    // show dialog
    const skips = {
      skipableToKickoff: approval.skipableToKickoff,
      skipableToDesignReview: approval.skipableToDesignReview,
      skipableToQualification: approval.skipableToQualification
    }
    yield put(updateSkipables(skips))
    yield put(updateWorkflowId(workflowId))
    yield put(updateProjectId(projectId))
    yield put(setDialogActive(value))
  }
}

function* watchSetDialogActive() {
  yield takeEvery(SET_DIALOG_ACTIVE, handleSetDialogActive)
}

function* watchSetDialogInactive() {
  yield takeEvery(SET_DIALOG_INACTIVE, handleSetDialogInactive)
}

function* watchSubmit() {
  yield takeLatest(SUBMIT, handleSubmit)
}

function* watchSubmitStart() {
  yield takeLatest(SUBMIT_START, handleSubmitStart)
}

function* loadSagas() {
  yield all([
    watchSetDialogActive(),
    watchSetDialogInactive(),
    watchSubmit(),
    watchSubmitStart(),
  ])
}

export default loadSagas
